import { useReducer, useCallback } from 'react';
import axios, { AxiosRequestConfig } from 'axios';
import { ApiReducer, ApiState } from './ApiReducer';

/**
 * useApi hook for making authenticated API calls.
 *
 * IMPORTANT: This hook relies on the Axios interceptor set up in useMsal.tsx
 * to add the Bearer token. Do NOT manually add the Authorization header here
 * as it creates a race condition with the interceptor's token acquisition.
 *
 * The interceptor calls acquireToken() for every request, ensuring a fresh
 * token is always used (like OrcaWebsite pattern).
 */
export default () => {
    const [state, dispatch] = useReducer(ApiReducer, {
        status: 'empty',
        isLoading: false,
        error: null,
        endpoint: null,
    } as ApiState);

    type GetProps = {
        endpoint: string;
        queryParam?: string | null;
        config?: AxiosRequestConfig | null;
        fullUrl?: string | null;
    };

    const getApiData = useCallback(async (props: GetProps): Promise<any> => {
        const { endpoint, queryParam = '', config, fullUrl } = props;

        try {
            dispatch({ type: 'request_started', endpoint });

            if (!endpoint && !fullUrl) {
                throw new Error('Missing url parameter');
            }

            const url = fullUrl || endpoint;
            const finalUrl = queryParam ? `${url}${queryParam}` : url;

            // Token is added by Axios interceptor in useMsal.tsx
            const response = await axios.get(finalUrl, config ?? {});

            dispatch({ type: 'request_success', result: response.data, endpoint });
            return response.data;
        } catch (error: any) {
            console.error('[useApi] GET request failed:', {
                url: fullUrl || endpoint,
                message: error.message,
                code: error.code,
                status: error.response?.status,
                statusText: error.response?.statusText,
                data: error.response?.data,
            });
            dispatch({ type: 'request_error', error: error, endpoint });
            throw error;
        }
    }, []);

    const postApiData = useCallback(async (props: GetProps & { data?: any }): Promise<any> => {
        const { endpoint, data, config, fullUrl } = props;

        try {
            dispatch({ type: 'request_started', endpoint });

            if (!endpoint && !fullUrl) {
                throw new Error('Missing url parameter');
            }

            const url = fullUrl || endpoint;

            const response = await axios.post(url, data, config ?? {});

            dispatch({ type: 'request_success', result: response.data, endpoint });
            return response.data;
        } catch (error: any) {
            console.error('API POST Error:', {
                url: fullUrl || endpoint,
                status: error.response?.status,
                statusText: error.response?.statusText,
                requestData: data,
                responseData: error.response?.data,
                headers: error.response?.headers,
                message: error.message,
            });

            const errorMessage = error.response?.data?.message || error.response?.statusText || error.message || 'Unknown error occurred';
            dispatch({ type: 'request_error', error: errorMessage, endpoint });
            throw error;
        }
    }, []);

    const putApiData = useCallback(async (props: GetProps & { data?: any }): Promise<any> => {
        const { endpoint, data, config, fullUrl } = props;

        try {
            dispatch({ type: 'request_started', endpoint });

            if (!endpoint && !fullUrl) {
                throw new Error('Missing url parameter');
            }

            const url = fullUrl || endpoint;

            const response = await axios.put(url, data, config ?? {});

            dispatch({ type: 'request_success', result: response.data, endpoint });
            return response.data;
        } catch (error: any) {
            console.error('API PUT Error:', {
                url: fullUrl || endpoint,
                status: error.response?.status,
                statusText: error.response?.statusText,
                data: error.response?.data,
                headers: error.response?.headers,
                message: error.message,
            });

            const errorMessage = error.response?.data?.message || error.response?.statusText || error.message || 'Unknown error occurred';
            dispatch({ type: 'request_error', error: errorMessage, endpoint });
            throw error;
        }
    }, []);

    return { state, getApiData, postApiData, putApiData };
};
